import SvgAdd from '@common/__generated__/icon/components/SvgAdd';
import { Logo } from '@common/components';
import { cn } from '@common/utils';
import { cva } from 'class-variance-authority';
import Image from 'next/image';

type AvatarSize = 24 | 32 | 40 | 48 | 56 | 64 | 80;
type AvatarType = 'person' | 'company' | 'wello' | 'wellobiz';

interface BaseAvatarProps {
  size?: AvatarSize;
  type?: AvatarType;
  className?: string;
}

// AvatarCoreProps는 src와 alt를 필수로 설정
interface AvatarCoreProps extends BaseAvatarProps {
  type: AvatarType;
  src?: string; // 단일 아바타 이미지
  alt: string; // 단일 아바타 설명
}

// AvatarProps는 src와 alt를 선택적으로 설정
interface AvatarProps extends BaseAvatarProps {
  showMore?: boolean; // '더보기' 버튼 표시 여부
  src?: string | string[]; // 단일 또는 다중 아바타 이미지
  alt?: string | string[]; // 단일 또는 다중 아바타 설명
  name?: string;
}

const PersonSvg = () => (
  <svg
    fill="none"
    height="108"
    viewBox="0 0 108 108"
    width="108"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_6090_25174)">
      <path
        clipRule="evenodd"
        d="M108 54C108 68.4787 102.301 81.6264 93.0258 91.3233C83.2287 101.564 69.4417 107.955 54.1612 108H54H53.8388C38.5583 107.955 24.771 101.564 14.9744 91.3233C5.69823 81.6264 0 68.4787 0 54C0 24.1766 24.1766 0 54 0C83.8234 0 108 24.1766 108 54ZM86.7302 81C78.9488 71.5768 67.1755 65.5712 54 65.5712C40.8245 65.5712 29.0515 71.5768 21.2694 81C29.0515 90.423 40.8245 96.4286 54 96.4286C67.1755 96.4286 78.9488 90.423 86.7302 81ZM54.0009 57.8569C64.6521 57.8569 73.2866 49.2224 73.2866 38.5712C73.2866 27.92 64.6521 19.2855 54.0009 19.2855C43.3497 19.2855 34.7152 27.92 34.7152 38.5712C34.7152 49.2224 43.3497 57.8569 54.0009 57.8569Z"
        fill="#E5E7EC"
        fillRule="evenodd"
      />
    </g>
    <defs>
      <clipPath id="clip0_6090_25174">
        <rect fill="white" height="108" width="108" />
      </clipPath>
    </defs>
  </svg>
);

const CompanySvg = () => (
  <svg
    fill="none"
    height="112"
    viewBox="0 0 112 112"
    width="112"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0 16C0 7.16344 7.16344 0 16 0H96C104.837 0 112 7.16344 112 16V96C112 104.837 104.837 112 96 112H16C7.16344 112 0 104.837 0 96V16Z"
      fill="#E5E7EC"
    />
    <path
      clipRule="evenodd"
      d="M29.8206 34.6249V77.3745H59.6512V34.6249H29.8206ZM27.0063 29H62.4654C64.0197 29 65.2796 30.2592 65.2796 31.8125V80.187C65.2796 81.7403 64.0196 82.9995 62.4654 82.9995H27.0063C25.4521 82.9995 24.1921 81.7403 24.1921 80.187V31.8125C24.1921 30.2592 25.4521 29 27.0063 29Z"
      fill="white"
      fillRule="evenodd"
    />
    <path
      clipRule="evenodd"
      d="M60.214 49.3428C60.214 47.7896 61.474 46.5304 63.0282 46.5304H84.457C86.0112 46.5304 87.2712 47.7896 87.2712 49.3428V80.072C87.2712 81.6253 86.0112 82.8845 84.457 82.8845H62.3453C60.7911 82.8845 59.5311 81.6253 59.5311 80.072C59.5311 78.5187 60.7911 77.2596 62.3453 77.2596H81.6427V52.1553H63.0282C61.474 52.1553 60.214 50.8961 60.214 49.3428Z"
      fill="white"
      fillRule="evenodd"
    />
    <path
      clipRule="evenodd"
      d="M44.6548 68.4739C46.209 68.4739 47.469 69.7331 47.469 71.2864V80.0661C47.469 81.6194 46.209 82.8786 44.6548 82.8786C43.1006 82.8786 41.8406 81.6194 41.8406 80.0661V71.2864C41.8406 69.7331 43.1006 68.4739 44.6548 68.4739Z"
      fill="white"
      fillRule="evenodd"
    />
    <path
      clipRule="evenodd"
      d="M35.2077 58.6826C35.2077 57.1294 36.4676 55.8702 38.0219 55.8702H51.2889C52.8431 55.8702 54.1031 57.1294 54.1031 58.6826C54.1031 60.2359 52.8431 61.4951 51.2889 61.4951H38.0219C36.4676 61.4951 35.2077 60.2359 35.2077 58.6826Z"
      fill="white"
      fillRule="evenodd"
    />
    <path
      clipRule="evenodd"
      d="M35.2077 45.5163C35.2077 43.9631 36.4676 42.7039 38.0219 42.7039H51.2889C52.8431 42.7039 54.1031 43.9631 54.1031 45.5163C54.1031 47.0696 52.8431 48.3288 51.2889 48.3288H38.0219C36.4676 48.3288 35.2077 47.0696 35.2077 45.5163Z"
      fill="white"
      fillRule="evenodd"
    />
  </svg>
);

const defaultImages: Record<AvatarType, () => JSX.Element> = {
  person: () => <PersonSvg />,
  company: () => <CompanySvg />,
  wello: () => <Logo name="wello" />,
  wellobiz: () => <Logo name="biz" />,
};

const avatarVariants = cva(
  'relative overflow-hidden bg-Gray-white flex-center',
  {
    variants: {
      type: {
        person: 'rounded-pill',
        wello: 'rounded-pill',
        wellobiz: 'rounded-pill',
        company: 'rounded-large',
      },
    },
    defaultVariants: {
      type: 'person',
    },
  },
);

const OVERLAP_OFFSET_RATIO = 1.4;

const AvatarCore = ({ size, type, alt, src, className }: AvatarCoreProps) => {
  const DefaultIcon = defaultImages[type];

  return (
    <div
      className={cn(avatarVariants({ type }), className)}
      style={{ width: size, height: size, minWidth: size, minHeight: size }}
    >
      {src ? (
        <Image
          fill
          alt={alt}
          className={cn('object-cover', 'user-none-drag')}
          src={src}
        />
      ) : (
        <DefaultIcon />
      )}
    </div>
  );
};

export const Avatar = ({
  size = 32,
  type = 'person',
  alt,
  src,
  className,
  name,
  showMore = false,
}: AvatarProps) => {
  // src가 문자열이 아닌 경우 다중 아바타로 처리
  const isMulti = Array.isArray(src);

  // 겹치지 않는 사이즈 계산
  const overlapOffset = Math.round(size / OVERLAP_OFFSET_RATIO);
  const nonOverlapSize = size - overlapOffset;

  // 다중 아바타의 src와 alt 배열 준비

  const multiSrc = isMulti ? (src as string[]) : [src];
  const multiAlt = Array.isArray(alt) ? alt : multiSrc.map(() => 'avatar');
  const singleAlt = Array.isArray(alt) ? alt[0] : (alt ?? 'avatar');
  if (!isMulti)
    return (
      <AvatarCore
        alt={singleAlt}
        className={className}
        size={size}
        src={src}
        type={type}
      />
    );

  return (
    <div className={cn('flex items-center', className)}>
      {multiSrc.map((avatarSrc, index) => (
        <div
          key={`${name ? `${name}-` : ''}${avatarSrc}-${index}`}
          style={{
            marginLeft: index ? -nonOverlapSize : 0,
          }}
        >
          <AvatarCore
            alt={multiAlt[index]}
            className="border-1 border-Gray-white"
            size={size}
            src={avatarSrc}
            type={type}
          />
        </div>
      ))}
      {showMore ? (
        <div
          className="relative rounded-pill border-1 border-Gray-white bg-Gray-300 flex-center"
          style={{
            marginLeft: -nonOverlapSize,
            width: size,
            height: size,
          }}
        >
          <SvgAdd className="size-8 text-Gray-600" />
        </div>
      ) : null}
    </div>
  );
};
